import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { Card, CardActions, Button } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { dataModel } from '../../dataModels/dataModel';
import { QuotesProvider } from '../../context/QuotesContext';
import { defaultCardContentProps, showBelowSmBreakpoint } from '../../constants/responsiveClassNames';
import QuotesHeader from './QuotesHeader';
import QuotesTabsView from './QuotesTabsView';
import QuotesNoTabsView from './QuotesNoTabsView';
import QuotesNullView from './QuotesNullView/QuotesNullView';
import QuotesLoading from './QuotesLoading';
import QuotesError from './QuotesError';
import { useQuoteContext } from '../../hooks/useQuoteContext';
import { triggerAnalyticsCustomEvent } from '../../utils/analyticsEvents';

const { number, string, bool } = proptypes;

const QuotesActions = ({ isMobile }) => {

  const viewAllQuotesClick = () => {
    triggerAnalyticsCustomEvent('quotes.stash-click', {
      component: 'view all'
    });
  };
  return isMobile ? (
    <div>
      <CardActions>
        <Button onClick={viewAllQuotesClick} fullWidth href="/quote/list">View All Quotes</Button>
      </CardActions>
    </div>
  ) : null;
};

const QuotesContent = () => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const {
    loading,
    error,
    hasQuotes,
    showTabs,
  } = useQuoteContext();

  // Loading skeleton
  if (loading) {
    return <QuotesLoading />;
  }

  // Error state
  if (error) {
    return <QuotesError />;
  }

  if (!hasQuotes) {
    return <QuotesNullView />;
  }

  let quotesGapClass = 'sui-gap-6';
  if (!isMobile) {
    quotesGapClass = 'sui-gap-8';
  } if (showTabs) {
    quotesGapClass = 'sui-gap-4';
  }

  return (
    <Card
      data-component="Quotes"
      data-testid="quotes-container"
      className="sui-h-full sui-w-full"
      CardContentProps={{ className: classNames(defaultCardContentProps, 'sui-h-full') }}
    >
      <div className="sui-@container sui-h-full" id="quotes">
        <div className={
          classNames(
            'sui-flex sui-flex-col sui-h-full',
            quotesGapClass
          )
        }
        >
          <QuotesHeader />
          <div
            className="sui-flex sui-flex-col sui-justify-between sui-grow"
          >
            {showTabs ? (
              <QuotesTabsView />
            ) : (
              <QuotesNoTabsView />
            )}
            <QuotesActions isMobile={isMobile} />
          </div>
        </div>
      </div>
    </Card>
  );
};

QuotesActions.propTypes = {
  isMobile: bool,
};

QuotesActions.defaultProps = {
  isMobile: false,
};

QuotesContent.propTypes = {};

QuotesContent.defaultProps = {};

const Quotes = ({ maxListSize, componentId }) => {

  const data = {
    name: 'Quotes',
    component: 'Quotes',
    type: 'content',
    position: '',
    id: componentId
  };

  return (
    <ImpressionProvider data={data}>
      <QuotesProvider
        maxListSize={maxListSize}
        componentId={componentId}
      >
        <QuotesContent />
      </QuotesProvider>
    </ImpressionProvider>
  );
};

Quotes.propTypes = {
  maxListSize: number,
  componentId: string,
};

Quotes.defaultProps = {
  maxListSize: 3,
  componentId: '',
};

Quotes.displayName = 'Quotes';
Quotes.dataModel = dataModel;

export { Quotes, QuotesContent };
